import { TextContentElement } from '@deltek/specification-client/core/Models/textContentElement'
import { SuggestionData } from 'ckeditor5-premium-features'
import { ContentItemViewNodesType } from 'components/WorkspaceEditor/types'

/** This module provides functions to detect changes in the content of a plain text editor */
export interface ChangeDetectionParams {
    /** The unsaved data in the editor */
    unsavedData: string

    /** The original text before any changes were made */
    originalText: string

    /** Optional node to add, which may contain an id and type */
    nodeToAdd?: { id?: string; type?: string } | null

    /** Previous content item view nodes, used to determine if we should revert */
    previousContentItemViewNodes?: Partial<ContentItemViewNodesType>[] | null

    /** Initial suggestions available in the editor */
    initialSuggestions: SuggestionData[]

    /** Flag indicating if the user has deleted the whole text */
    hasDeletedWholeText: boolean

    /** stores the currently being added text content element */
    textContentElementToAdd: Partial<TextContentElement> | null

    /** indicates whether text is currently being edited in PlainText */
    isEditingPlainText: boolean
}

/** Interface for the result of change detection in the editor */
export interface ChangeDetectionResult {
    /** Indicates if there are changes in the content */
    hasChanges: boolean

    /** Indicates if we should revert to the previous content state */
    shouldRevertToPrevious: boolean

    /** Indicates if the editor should be deactivated */
    shouldDeactivate: boolean

    /** Indicates if new content should be added */
    shouldAddNewContent: boolean

    /** Indicates if an optimistic update should be executed */
    shouldExecuteOptimistic: boolean
}

/**
 * Checks if content update should be skipped.
 * Skip when content is empty or unchanged.
 * Special case: If original was empty and final is empty, skip (no net change).
 * But if original had text and final is empty, process (actual deletion).
 */
const shouldSkipContentUpdate = (
    unsavedDataString: string,
    originalText: string,
    hasDeletedWholeText: boolean
) => {
    // If both original and current are empty, skip (no net change)
    // This handles: empty → type → delete all → blur
    if (!originalText && !unsavedDataString) {
        return true // Skip - no net change
    }

    // IMPORTANT: If unsavedData is empty but original has text, only process if user actually deleted
    // The empty unsavedData could mean "no changes" rather than "deleted all"
    if (originalText && !unsavedDataString) {
        // Only process deletion if hasDeletedWholeText flag is true
        // This flag should only be true when user actively deleted the text
        if (hasDeletedWholeText) {
            return false // Don't skip - process deletion
        } else {
            return true // Skip - no actual changes (empty means unchanged)
        }
    }

    // Skip if content is unchanged
    const hasNoMeaningfulChange = unsavedDataString === originalText

    return hasNoMeaningfulChange
}

const shouldRevertToPrevious = (
    nodeToAdd?: { id?: string } | null,
    previousContentItemViewNodes?: Partial<ContentItemViewNodesType>[] | null
) => {
    return Boolean(nodeToAdd?.id && previousContentItemViewNodes)
}

/**
 * Determines if editor should remain active based on suggestions and deletion state
 */
const shouldKeepEditorActive = (
    hasSuggestions: boolean,
    hasDeletedWholeText: boolean
) => {
    // Keep active only if we have both suggestions AND deleted text
    return hasSuggestions && hasDeletedWholeText
}

/**
 * Determines what action should be taken based on the current state of the editor
 * @param params - The parameters for change detection
 */
export const detectContentChanges = (
    params: ChangeDetectionParams
): ChangeDetectionResult => {
    const {
        unsavedData,
        originalText,
        nodeToAdd,
        previousContentItemViewNodes,
        initialSuggestions,
        hasDeletedWholeText,
        textContentElementToAdd,
        isEditingPlainText,
    } = params
    // Check if editor is fully deactivated (not editing and no pending content)
    const shouldDeactivateContentEditor =
        textContentElementToAdd === null && isEditingPlainText === false
    const skipUpdate = shouldSkipContentUpdate(
        unsavedData,
        originalText,
        hasDeletedWholeText
    )
    const hasSuggestions = initialSuggestions.length > 0
    const shouldRevert = Boolean(
        nodeToAdd?.id && nodeToAdd?.type && previousContentItemViewNodes
    )

    const baseResult: ChangeDetectionResult = {
        hasChanges: !skipUpdate,
        shouldRevertToPrevious: false,
        shouldDeactivate: false,
        shouldAddNewContent: false,
        shouldExecuteOptimistic: false,
    }

    // Case 1: Skip content update
    if (skipUpdate || shouldDeactivateContentEditor) {
        // If editor is deactivated, indicate that deactivation should happen
        if (shouldDeactivateContentEditor) {
            return { ...baseResult, hasChanges: false, shouldDeactivate: true }
        }

        if (shouldRevert) {
            return { ...baseResult, shouldRevertToPrevious: true }
        }

        const keepActive = shouldKeepEditorActive(
            hasSuggestions,
            hasDeletedWholeText
        )
        return {
            ...baseResult,
            shouldDeactivate: !keepActive,
            shouldExecuteOptimistic: keepActive,
        }
    }

    // Case 2: Has changes - check if adding new content
    if (nodeToAdd?.id && nodeToAdd?.type) {
        return { ...baseResult, hasChanges: true, shouldAddNewContent: true }
    }

    // Case 3: Has changes - execute optimistic update
    return { ...baseResult, hasChanges: true, shouldExecuteOptimistic: true }
}

/**
 * Helper to check if content has meaningful changes
 */
export const hasContentChanged = (
    unsavedData: string,
    originalText: string
) => {
    return Boolean(unsavedData) && unsavedData !== originalText
}
